import React, { useState } from 'react';
import { PlannedItem, Transaction, PlannedMatchGroup, PlannedConcept } from '../../types';
import { matchTransactionsToItems, unmatchGroup, updatePlannedItem } from '../../services/planningService';
import { Link, Check, X, ArrowLeftRight, CheckCircle2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { format, subMonths, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';

interface MatchViewProps {
  items: PlannedItem[];
  concepts: PlannedConcept[];
  transactions: Transaction[];
  matches: PlannedMatchGroup[];
  userId: string;
}

export const MatchView: React.FC<MatchViewProps> = ({ items, concepts, transactions, matches, userId }) => {
  const [selectedTxIds, setSelectedTxIds] = useState<Set<string>>(new Set());
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());

  const [txSearch, setTxSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [txTypeFilter, setTxTypeFilter] = useState<'ALL' | 'INCOME' | 'EXPENSE'>('ALL');
  const [itemTypeFilter, setItemTypeFilter] = useState<'ALL' | 'INCOME' | 'EXPENSE' | 'ACCUMULATIVE'>('ALL');
  const [txMonthFilter, setTxMonthFilter] = useState<string>(format(new Date(), 'yyyy-MM'));
  const [itemMonthFilter, setItemMonthFilter] = useState<string>(format(new Date(), 'yyyy-MM'));

  const [txSortField, setTxSortField] = useState<'date' | 'amount'>('date');
  const [txSortDir, setTxSortDir] = useState<'asc' | 'desc'>('desc');
  const [itemSortField, setItemSortField] = useState<'date' | 'amount'>('date');
  const [itemSortDir, setItemSortDir] = useState<'asc' | 'desc'>('asc');

  const [txAmountMode, setTxAmountMode] = useState<'exact' | 'range'>('range');
  const [txAmountExact, setTxAmountExact] = useState<string>('');
  const [txAmountMin, setTxAmountMin] = useState<string>('');
  const [txAmountMax, setTxAmountMax] = useState<string>('');

  const [itemAmountMode, setItemAmountMode] = useState<'exact' | 'range'>('range');
  const [itemAmountExact, setItemAmountExact] = useState<string>('');
  const [itemAmountMin, setItemAmountMin] = useState<string>('');
  const [itemAmountMax, setItemAmountMax] = useState<string>('');

  const MATCHES_BATCH = 10;
  const [showMatches, setShowMatches] = useState(false);
  const [matchesVisible, setMatchesVisible] = useState(MATCHES_BATCH);

  const passAmount = (amount: number, mode: 'exact' | 'range', exact: string, min: string, max: string) => {
    if (mode === 'exact') {
      if (exact === '') return true;
      return amount === Number(exact);
    }
    const mn = min === '' ? -Infinity : Number(min);
    const mx = max === '' ? Infinity : Number(max);
    return amount >= mn && amount <= mx;
  };

  const copyAmountToItems = () => {
    setItemAmountMode(txAmountMode);
    setItemAmountExact(txAmountExact);
    setItemAmountMin(txAmountMin);
    setItemAmountMax(txAmountMax);
  };

  const copyAmountToTx = () => {
    setTxAmountMode(itemAmountMode);
    setTxAmountExact(itemAmountExact);
    setTxAmountMin(itemAmountMin);
    setTxAmountMax(itemAmountMax);
  };

  const handlePrevTxMonth = () => {
    const d = txMonthFilter ? new Date(txMonthFilter + '-01T12:00:00') : new Date();
    setTxMonthFilter(format(subMonths(d, 1), 'yyyy-MM'));
  };

  const handleNextTxMonth = () => {
    const d = txMonthFilter ? new Date(txMonthFilter + '-01T12:00:00') : new Date();
    setTxMonthFilter(format(addMonths(d, 1), 'yyyy-MM'));
  };

  const handlePrevMonth = () => {
    const currentDate = itemMonthFilter ? new Date(itemMonthFilter + '-01T12:00:00') : new Date();
    setItemMonthFilter(format(subMonths(currentDate, 1), 'yyyy-MM'));
  };

  const handleNextMonth = () => {
    const currentDate = itemMonthFilter ? new Date(itemMonthFilter + '-01T12:00:00') : new Date();
    setItemMonthFilter(format(addMonths(currentDate, 1), 'yyyy-MM'));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const handleTxToggle = (id: string) => {
    const newKeys = new Set(selectedTxIds);
    if (newKeys.has(id)) newKeys.delete(id);
    else newKeys.add(id);
    setSelectedTxIds(newKeys);
  };

  const handleItemToggle = (id: string) => {
    const newKeys = new Set(selectedItemIds);
    if (newKeys.has(id)) newKeys.delete(id);
    else newKeys.add(id);
    setSelectedItemIds(newKeys);
  };

  const handleMatchTotal = async () => {
    if (selectedTxIds.size === 0 || selectedItemIds.size === 0) return;
    
    // Find which of the selected items are accumulative
    const accumulativeIds = Array.from(selectedItemIds).filter(id => {
      const item = items.find(i => i.id === id);
      if (!item) return false;
      const concept = concepts.find(c => c.id === item.conceptId);
      return concept?.isAccumulative;
    }) as string[];

    await matchTransactionsToItems(userId, Array.from(selectedTxIds), Array.from(selectedItemIds), accumulativeIds, false);
    
    setSelectedTxIds(new Set());
    setSelectedItemIds(new Set());
  };

  const handleMatchPartial = async () => {
    if (selectedTxIds.size === 0 || selectedItemIds.size === 0) return;
    
    // Is partial => stays PENDING
    await matchTransactionsToItems(userId, Array.from(selectedTxIds), Array.from(selectedItemIds), [], true);
    
    setSelectedTxIds(new Set());
    setSelectedItemIds(new Set());
  };

  const handleForceComplete = async (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    await updatePlannedItem(userId, itemId, { status: 'COMPLETED' });
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  };

  const pendingItems = items.filter(i => {
    if (i.status !== 'PENDING') return false;
    if (itemSearch && !i.title.toLowerCase().includes(itemSearch.toLowerCase())) return false;
    
    if (itemTypeFilter === 'INCOME' && i.type !== 'INCOME') return false;
    if (itemTypeFilter === 'EXPENSE' && i.type !== 'EXPENSE') return false;
    if (itemTypeFilter === 'ACCUMULATIVE') {
      const isAccum = concepts.find(c => c.id === i.conceptId)?.isAccumulative;
      if (!isAccum) return false;
    }

    if (itemMonthFilter && !i.date.startsWith(itemMonthFilter)) return false;

    if (!passAmount(i.amount, itemAmountMode, itemAmountExact, itemAmountMin, itemAmountMax)) return false;

    return true; // As long as it is PENDING, we show it
  }).sort((a, b) => {
    let cmp = 0;
    if (itemSortField === 'amount') {
      cmp = a.amount - b.amount;
    } else {
      cmp = a.date.localeCompare(b.date);
      if (cmp === 0) cmp = (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
      if (cmp === 0 && a.type !== b.type) cmp = a.type === 'INCOME' ? -1 : 1;
    }
    return itemSortDir === 'asc' ? cmp : -cmp;
  });
  
  // Exclude transactions that are already part of a match or are transfers
  const matchedTxIds = new Set(matches.flatMap(m => m.transactionIds));
  const availableTx = transactions.filter(t => {
    if (t.type === 'TRANSFER') return false;
    if (matchedTxIds.has(t.id)) return false;
    if (txSearch && !t.description.toLowerCase().includes(txSearch.toLowerCase())) return false;
    if (txMonthFilter && !t.date.startsWith(txMonthFilter)) return false;

    if (txTypeFilter === 'INCOME' && t.type !== 'INCOME') return false;
    if (txTypeFilter === 'EXPENSE' && t.type !== 'EXPENSE') return false;

    const amount = t.details.reduce((s, d) => s + d.amount, 0);
    if (!passAmount(amount, txAmountMode, txAmountExact, txAmountMin, txAmountMax)) return false;

    return true;
  }).sort((a, b) => {
    const am = (tx: Transaction) => tx.details.reduce((s, d) => s + d.amount, 0);
    let cmp = txSortField === 'amount' ? (am(a) - am(b)) : a.date.localeCompare(b.date);
    return txSortDir === 'asc' ? cmp : -cmp;
  });

  const getMatchWarning = (): string | null => {
    if (selectedTxIds.size === 0 || selectedItemIds.size === 0) return null;

    let selectedType: string | null = null;
    let isAccumulativeMixed = false;

    // Check planned items
    for (const id of selectedItemIds) {
      const item = items.find(i => i.id === id);
      if (item) {
        const concept = concepts.find(c => c.id === item.conceptId);
        if (concept?.isAccumulative) {
          isAccumulativeMixed = true;
          continue; // Accumulatives don't have strict type constraint for mixing
        }
        if (!selectedType) selectedType = item.type;
        else if (selectedType !== item.type) return "Conflicto: No puedes mezclar ingresos y egresos planificados.";
      }
    }

    // Check transactions
    for (const id of selectedTxIds) {
      const tx = transactions.find(t => t.id === id);
      if (tx) {
        if (!selectedType) selectedType = tx.type;
        else if (selectedType !== tx.type && !isAccumulativeMixed) return "Conflicto: Los registros y los planificados deben ser del mismo tipo (solo ingresos o solo egresos).";
      }
    }

    return null;
  };

  const matchWarning = getMatchWarning();
  const canMatch = selectedTxIds.size > 0 && selectedItemIds.size > 0 && matchWarning === null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        {/* Left: Real Transactions */}
        <div className="flex-1 bg-zinc-800/20 border border-zinc-700/50 rounded-xl flex flex-col">
          <div className="p-4 border-b border-zinc-700/50 bg-zinc-800/30">
            <h3 className="font-bold text-sm text-zinc-300 flex items-center gap-1.5">
              Registros Operativos (Reales) <span className="text-xs font-bold text-zinc-500">({availableTx.length})</span>
            </h3>
            <p className="text-xs text-zinc-500 mb-3">Selecciona uno o más gastos/ingresos reales</p>
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1 slim-scrollbar">
              <button 
                onClick={() => setTxTypeFilter('ALL')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${txTypeFilter === 'ALL' ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Todos
              </button>
              <button 
                onClick={() => setTxTypeFilter('INCOME')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${txTypeFilter === 'INCOME' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Ingresos
              </button>
              <button 
                onClick={() => setTxTypeFilter('EXPENSE')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${txTypeFilter === 'EXPENSE' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Egresos
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Buscar registro..."
                value={txSearch}
                onChange={(e) => setTxSearch(e.target.value)}
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors w-0"
              />
              <div className="flex items-center bg-zinc-900 border border-zinc-700 rounded-lg shrink-0">
                <button onClick={handlePrevTxMonth} className="px-2 py-1.5 hover:bg-zinc-800 rounded-l-lg text-zinc-400 transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <div 
                  className="px-2 text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:text-indigo-400 transition-colors w-24 text-center select-none"
                  onClick={() => setTxMonthFilter('')}
                  title={txMonthFilter ? "Click para mostrar todos los meses" : "Mostrando todos los meses"}
                >
                  {txMonthFilter ? format(new Date(txMonthFilter + '-01T12:00:00'), 'MMM yyyy', { locale: es }) : 'TODOS'}
                </div>
                <button onClick={handleNextTxMonth} className="px-2 py-1.5 hover:bg-zinc-800 rounded-r-lg text-zinc-400 transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <div className="flex rounded-lg border border-zinc-700 overflow-hidden shrink-0 bg-zinc-900">
                <button
                  type="button"
                  onClick={() => setTxAmountMode('exact')}
                  className={`px-2 py-1 text-xs font-bold transition-all ${
                    txAmountMode === 'exact' 
                      ? 'bg-zinc-700 text-white' 
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  Exacto
                </button>
                <button
                  type="button"
                  onClick={() => setTxAmountMode('range')}
                  className={`px-2 py-1 text-xs font-bold transition-all ${
                    txAmountMode === 'range' 
                      ? 'bg-zinc-700 text-white' 
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  Rango
                </button>
              </div>

              {txAmountMode === 'exact' ? (
                <input
                  type="number"
                  placeholder="Importe..."
                  value={txAmountExact}
                  onChange={(e) => setTxAmountExact(e.target.value)}
                  className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                />
              ) : (
                <div className="flex-1 flex gap-1.5 min-w-0">
                  <input
                    type="number"
                    placeholder="Desde..."
                    value={txAmountMin}
                    onChange={(e) => setTxAmountMin(e.target.value)}
                    className="w-1/2 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                  <input
                    type="number"
                    placeholder="Hasta..."
                    value={txAmountMax}
                    onChange={(e) => setTxAmountMax(e.target.value)}
                    className="w-1/2 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
              )}

              {(txAmountExact !== '' || txAmountMin !== '' || txAmountMax !== '') && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={copyAmountToItems}
                    className="text-xs text-zinc-400 hover:text-indigo-400 flex items-center gap-1 transition-colors px-1 py-1"
                    title="Copiar a Pendientes"
                  >
                    <span>Copiar</span>
                    <ArrowRight size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTxAmountExact('');
                      setTxAmountMin('');
                      setTxAmountMax('');
                    }}
                    className="p-1 text-zinc-400 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Limpiar filtro de importe"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t border-zinc-700/30">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mr-1">Ordenar:</span>
              <button
                type="button"
                onClick={() => setTxSortField('date')}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${txSortField === 'date' ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Fecha
              </button>
              <button
                type="button"
                onClick={() => setTxSortField('amount')}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${txSortField === 'amount' ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Importe
              </button>
              <button
                type="button"
                onClick={() => setTxSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="px-2.5 py-1 rounded text-xs font-bold bg-zinc-800 text-zinc-400 hover:text-zinc-300 transition-colors flex items-center justify-center ml-auto"
                title={txSortDir === 'asc' ? 'Ascendente' : 'Descendente'}
              >
                {txSortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              </button>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {availableTx.map(tx => {
              const amount = tx.details.reduce((acc, d) => acc + d.amount, 0);
              const isSelected = selectedTxIds.has(tx.id);
              return (
                <div 
                  key={tx.id}
                  onClick={() => handleTxToggle(tx.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected 
                      ? 'bg-indigo-500/10 border-indigo-500/40 opacity-100' 
                      : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600 opacity-80'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-sm font-medium text-white">{tx.description}</h4>
                      <p className="text-[10px] text-zinc-500">{format(new Date(tx.date), 'dd/MM/yyyy')}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${tx.type === 'INCOME' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(amount)}
                      </p>
                      {isSelected && <Check size={14} className="inline-block text-indigo-400 mt-1" />}
                    </div>
                  </div>
                </div>
              );
            })}
            {availableTx.length === 0 && (
              <div className="flex flex-col items-center justify-center p-8 text-center text-zinc-500 h-full">
                <p>No tienes registros operativos disponibles.</p>
              </div>
            )}
          </div>
        </div>

        {/* Center: Match Buttons */}
        <div className="flex flex-col items-center justify-center gap-4 relative md:w-24">
          <button
            onClick={handleMatchTotal}
            disabled={!canMatch}
            className="w-14 h-14 bg-indigo-500 hover:bg-indigo-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-black rounded-full flex items-center justify-center shadow-lg transition-colors cursor-pointer group relative"
            title={matchWarning || "Match Total (Liquidar)"}
          >
            <ArrowLeftRight size={24} />
            <span className="absolute -top-6 text-[10px] bg-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-white font-bold pointer-events-none z-10">Match Total</span>
          </button>
          
          <button
            onClick={handleMatchPartial}
            disabled={!canMatch}
            className="w-10 h-10 bg-indigo-500/20 hover:bg-indigo-500/40 border border-indigo-500 disabled:bg-zinc-800/10 disabled:border-zinc-700 disabled:text-zinc-600 text-indigo-400 rounded-full flex items-center justify-center shadow-lg transition-colors cursor-pointer group relative"
            title={matchWarning || "Match Parcial (No liquidar)"}
          >
            <ArrowLeftRight size={16} />
            <span className="absolute -bottom-6 text-[10px] bg-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-white font-bold pointer-events-none z-10">Match Parcial</span>
          </button>

          {matchWarning && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 mt-24 text-[10px] text-red-400 text-center bg-red-500/10 border border-red-500/20 p-2 rounded-xl">
              {matchWarning}
            </div>
          )}
        </div>

        {/* Right: Planned Items */}
        <div className="flex-1 bg-zinc-800/20 border border-zinc-700/50 rounded-xl flex flex-col">
          <div className="p-4 border-b border-zinc-700/50 bg-zinc-800/30">
            <h3 className="font-bold text-sm text-zinc-300 flex items-center gap-1.5">
              Planificados (Pendientes) <span className="text-xs font-bold text-zinc-500">({pendingItems.length})</span>
            </h3>
            <p className="text-xs text-zinc-500 mb-3">Selecciona uno o más conceptos planificados</p>
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1 slim-scrollbar">
              <button 
                onClick={() => setItemTypeFilter('ALL')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${itemTypeFilter === 'ALL' ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Todos
              </button>
              <button 
                onClick={() => setItemTypeFilter('INCOME')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${itemTypeFilter === 'INCOME' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Ingresos
              </button>
              <button 
                onClick={() => setItemTypeFilter('EXPENSE')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${itemTypeFilter === 'EXPENSE' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Egresos
              </button>
              <button 
                onClick={() => setItemTypeFilter('ACCUMULATIVE')}
                className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${itemTypeFilter === 'ACCUMULATIVE' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Acumulativos
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Buscar planificado..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors w-0"
                title="Búsqueda por texto"
              />
              <div className="flex items-center bg-zinc-900 border border-zinc-700 rounded-lg shrink-0">
                <button onClick={handlePrevMonth} className="px-2 py-1.5 hover:bg-zinc-800 rounded-l-lg text-zinc-400 transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <div 
                  className="px-2 text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:text-indigo-400 transition-colors w-24 text-center select-none"
                  onClick={() => setItemMonthFilter('')}
                  title={itemMonthFilter ? "Click para mostrar todos los meses" : "Mostrando todos los meses"}
                >
                  {itemMonthFilter ? format(new Date(itemMonthFilter + '-01T12:00:00'), 'MMM yyyy', { locale: es }) : 'TODOS'}
                </div>
                <button onClick={handleNextMonth} className="px-2 py-1.5 hover:bg-zinc-800 rounded-r-lg text-zinc-400 transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <div className="flex rounded-lg border border-zinc-700 overflow-hidden shrink-0 bg-zinc-900">
                <button
                  type="button"
                  onClick={() => setItemAmountMode('exact')}
                  className={`px-2 py-1 text-xs font-bold transition-all ${
                    itemAmountMode === 'exact' 
                      ? 'bg-zinc-700 text-white' 
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  Exacto
                </button>
                <button
                  type="button"
                  onClick={() => setItemAmountMode('range')}
                  className={`px-2 py-1 text-xs font-bold transition-all ${
                    itemAmountMode === 'range' 
                      ? 'bg-zinc-700 text-white' 
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  Rango
                </button>
              </div>

              {itemAmountMode === 'exact' ? (
                <input
                  type="number"
                  placeholder="Importe..."
                  value={itemAmountExact}
                  onChange={(e) => setItemAmountExact(e.target.value)}
                  className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                />
              ) : (
                <div className="flex-1 flex gap-1.5 min-w-0">
                  <input
                    type="number"
                    placeholder="Desde..."
                    value={itemAmountMin}
                    onChange={(e) => setItemAmountMin(e.target.value)}
                    className="w-1/2 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                  <input
                    type="number"
                    placeholder="Hasta..."
                    value={itemAmountMax}
                    onChange={(e) => setItemAmountMax(e.target.value)}
                    className="w-1/2 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
              )}

              {(itemAmountExact !== '' || itemAmountMin !== '' || itemAmountMax !== '') && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={copyAmountToTx}
                    className="text-xs text-zinc-400 hover:text-indigo-400 flex items-center gap-1 transition-colors px-1 py-1"
                    title="Copiar a Reales"
                  >
                    <ArrowLeft size={14} />
                    <span>Copiar</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setItemAmountExact('');
                      setItemAmountMin('');
                      setItemAmountMax('');
                    }}
                    className="p-1 text-zinc-400 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Limpiar filtro de importe"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t border-zinc-700/30">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mr-1">Ordenar:</span>
              <button
                type="button"
                onClick={() => setItemSortField('date')}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${itemSortField === 'date' ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Fecha
              </button>
              <button
                type="button"
                onClick={() => setItemSortField('amount')}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${itemSortField === 'amount' ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-300'}`}
              >
                Importe
              </button>
              <button
                type="button"
                onClick={() => setItemSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="px-2.5 py-1 rounded text-xs font-bold bg-zinc-800 text-zinc-400 hover:text-zinc-300 transition-colors flex items-center justify-center ml-auto"
                title={itemSortDir === 'asc' ? 'Ascendente' : 'Descendente'}
              >
                {itemSortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              </button>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {pendingItems.map(item => {
              const isSelected = selectedItemIds.has(item.id);
              
              // Determine how much is tracked so far
              const concept = concepts.find(c => c.id === item.conceptId);
              const isAccumulative = concept?.isAccumulative || false;
              let accumulatedAmount = 0;
              const matchGroups = matches.filter(m => m.plannedItemIds.includes(item.id));
              
              const isPartiallyMatched = matchGroups.length > 0 && !isAccumulative && item.status === 'PENDING';

              if (isAccumulative || isPartiallyMatched) {
                matchGroups.forEach(m => {
                  m.transactionIds.forEach(tid => {
                    const t = transactions.find(tr => tr.id === tid);
                    if (t) {
                      const amount = t.details.reduce((acc, d) => acc + d.amount, 0);
                      accumulatedAmount += amount;
                    }
                  });
                });
              }

              return (
                <div 
                  key={item.id}
                  onClick={() => handleItemToggle(item.id)}
                  className={`group p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected 
                      ? 'bg-emerald-500/10 border-emerald-500/40 opacity-100' 
                      : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600 opacity-80'
                  }`}
                >
                   <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-sm font-medium text-white flex items-center gap-2">
                        {item.title}
                        {isPartiallyMatched && <span className="px-1.5 py-0.5 rounded text-[8px] bg-sky-500/20 text-sky-400 font-bold uppercase tracking-wider">Parcial</span>}
                      </h4>
                      <p className="text-[10px] text-zinc-500">{item.date}</p>
                      {(isAccumulative || isPartiallyMatched) && accumulatedAmount > 0 && (
                        <p className={`text-[10px] font-bold mt-1 ${isPartiallyMatched ? 'text-sky-400' : 'text-zinc-400'}`}>
                          {isPartiallyMatched ? 'Pagado/Cobrado parcial:' : 'Acumulado:'} {formatCurrency(accumulatedAmount)}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex items-center justify-end gap-3">
                      <button
                        onClick={(e) => handleForceComplete(e, item.id)}
                        className="opacity-0 lg:group-hover:opacity-100 p-1.5 text-zinc-500 hover:text-emerald-400 hover:bg-emerald-400/10 rounded transition-all"
                        title="Finalizar (Cerrar / Liquidar)"
                      >
                        <CheckCircle2 size={16} />
                      </button>
                      <div className="flex flex-col items-end">
                        <p className={`text-sm font-bold ${item.type === 'INCOME' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(item.amount)}
                        </p>
                        {isSelected && <Check size={14} className="inline-block text-emerald-400 mt-1" />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingItems.length === 0 && (
              <div className="flex flex-col items-center justify-center p-8 text-center text-zinc-500 h-full">
                <p>No tienes ítems planificados pendientes para conciliar.</p>
                <p className="text-xs mt-2">Ve a 'Flujo de Caja' y proyecta tus conceptos.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Matched List */}
      {matches.length > 0 && (
        <div className="mt-8">
          <button
            type="button"
            onClick={() => {
              const nextVal = !showMatches;
              setShowMatches(nextVal);
              if (!nextVal) {
                setMatchesVisible(MATCHES_BATCH);
              }
            }}
            className="w-full flex justify-between items-center text-lg font-bold text-white bg-zinc-900 border border-zinc-800 rounded-xl px-5 py-3 hover:bg-zinc-800/60 transition-colors"
          >
            <span>Matches Completados ({matches.length})</span>
            {showMatches ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {showMatches && (
            <div className="mt-4 space-y-3">
              {[...matches]
                .sort((a, b) => {
                  const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                  const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                  return dateB - dateA;
                })
                .slice(0, matchesVisible)
                .map(m => {
                  const matchItems = items.filter(i => m.plannedItemIds.includes(i.id));
                  const matchTxs = transactions.filter(t => m.transactionIds.includes(t.id));
                  
                  const totalPlanned = matchItems.reduce((acc, i) => acc + i.amount, 0);
                  const totalReal = matchTxs.reduce((acc, t) => acc + t.details.reduce((a, d) => a + d.amount, 0), 0);
    
                  return (
                    <div key={m.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex flex-col md:flex-row justify-between items-start gap-4 group">
                      <div className="flex-1 w-full">
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-3">
                          <div className="flex flex-col">
                            <p className="text-sm text-zinc-300">
                              <span className="font-bold text-indigo-400">{matchTxs.length} registros</span> conciliados con <span className="font-bold text-emerald-400">{matchItems.length} planificados</span>
                            </p>
                            <p className="text-[10px] text-zinc-500 font-medium">
                              {m.createdAt ? format(new Date(m.createdAt), "dd/MM/yyyy HH:mm 'hs'") : 'Fecha no registrada'}
                            </p>
                          </div>
                          <div className="flex gap-4 text-[10px] font-mono bg-black/30 px-3 py-1.5 rounded-lg border border-zinc-800">
                            <span className="text-zinc-500">Total Real: <span className="text-emerald-400 font-bold">{formatCurrency(totalReal)}</span></span>
                            <span className="text-zinc-500">Total Plan: <span className="text-indigo-400 font-bold">{formatCurrency(totalPlanned)}</span></span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 pt-3 border-t border-zinc-800/50">
                        <div className="space-y-1">
                          <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Registros Reales</p>
                          {matchTxs.map(tx => (
                            <div key={tx.id} className="flex justify-between items-center text-xs text-zinc-400 bg-black/20 px-2 py-1.5 rounded">
                              <span className="truncate max-w-[180px]" title={tx.description}>{tx.description}</span>
                              <span className="font-bold text-zinc-300 ml-2 whitespace-nowrap">{formatCurrency(tx.details.reduce((s,d) => s + d.amount, 0))}</span>
                            </div>
                          ))}
                          {matchTxs.length < m.transactionIds.length && (
                            <p className="text-[9px] text-red-500/70 italic mt-1">Nota: {m.transactionIds.length - matchTxs.length} registro(s) no encontrado(s)</p>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Planificados</p>
                          {matchItems.map(item => (
                            <div key={item.id} className="flex justify-between items-center text-xs text-zinc-400 bg-black/20 px-2 py-1.5 rounded border-l-2 border-emerald-500/30">
                              <span className="truncate max-w-[180px]" title={item.title}>{item.title}</span>
                              <span className="font-bold text-zinc-300 ml-2 whitespace-nowrap">{formatCurrency(item.amount)}</span>
                            </div>
                          ))}
                          {matchItems.length < m.plannedItemIds.length && (
                            <p className="text-[9px] text-red-500/70 italic mt-1">Nota: {m.plannedItemIds.length - matchItems.length} planificado(s) no encontrado(s)</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => unmatchGroup(userId, m)}
                      className="p-2.5 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors opacity-100 lg:[@media(hover:hover)]:opacity-0 lg:group-hover:opacity-100 self-center md:self-start"
                      title="Deshacer Match"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  );
                })}

              {matchesVisible < matches.length && (
                <div className="flex flex-col items-center gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setMatchesVisible(prev => prev + MATCHES_BATCH)}
                    className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-bold rounded-lg px-4 py-2 transition-colors mx-auto"
                  >
                    Cargar más
                  </button>
                  <span className="text-xs text-zinc-500">
                    Mostrando {Math.min(matchesVisible, matches.length)} de {matches.length}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
